<template>
  <div
    class="message-bubble"
    :class="bubbleClasses"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
  >
    <!-- Avatar pour les messages des autres -->
    <div v-if="showAvatar && !isOwnMessage" class="message-avatar">
      <img
        v-if="message.userAvatar"
        :src="message.userAvatar"
        :alt="message.userName"
        class="avatar-image"
      />
      <div v-else class="avatar-placeholder">
        {{ userInitials }}
      </div>
    </div>

    <div class="message-content">
      <!-- Nom d'utilisateur pour les messages des autres -->
      <div v-if="showUserName" class="message-username">
        {{ message.userName }}
      </div>

      <!-- Réponse à un message -->
      <div v-if="message.replyTo && repliedMessage" class="message-reply">
        <div class="reply-indicator">↩️ Replying to {{ repliedMessage.userName }}</div>
        <div class="reply-text">{{ repliedMessage.text }}</div>
      </div>

      <!-- Contenu du message -->
      <div class="message-body">
        <!-- Message texte -->
        <div v-if="message.type === 'text'" class="message-text">
          <span v-if="isEditing" class="edit-input">
            <input
              v-model="editText"
              @keyup.enter="saveEdit"
              @keyup.escape="cancelEdit"
              @blur="cancelEdit"
              class="edit-field"
              ref="editInput"
            />
          </span>
          <span v-else>{{ message.text }}</span>
        </div>

        <!-- Message système -->
        <div v-else-if="message.type === 'system'" class="message-system">
          {{ message.text }}
        </div>

        <!-- Pièces jointes -->
        <div v-if="message.attachments && message.attachments.length" class="message-attachments">
          <div
            v-for="attachment in message.attachments"
            :key="attachment.id"
            class="attachment"
          >
            <img
              v-if="attachment.type.startsWith('image/')"
              :src="attachment.url || attachment.thumbnail"
              :alt="attachment.name"
              class="attachment-image"
              @click="previewAttachment(attachment)"
            />
            <div v-else class="attachment-file">
              <div class="file-icon">📄</div>
              <div class="file-info">
                <div class="file-name">{{ attachment.name }}</div>
                <div class="file-size">{{ formatFileSize(attachment.size) }}</div>
              </div>
              <button
                @click="downloadAttachment(attachment)"
                class="download-button"
                title="Download"
              >
                ⬇️
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Réactions -->
      <div v-if="message.reactions && message.reactions.length" class="message-reactions">
        <button
          v-for="reaction in message.reactions"
          :key="reaction.emoji"
          class="reaction-button"
          :class="{ 'reaction-button--active': hasUserReacted(reaction) }"
          @click="toggleReaction(reaction)"
          :title="getReactionUsers(reaction)"
        >
          <span class="reaction-emoji">{{ reaction.emoji }}</span>
          <span class="reaction-count">{{ reaction.count }}</span>
        </button>
      </div>

      <!-- Actions rapides -->
      <div v-if="showActions" class="message-actions">
        <button
          v-for="action in availableActions"
          :key="action.id"
          @click="handleAction(action)"
          class="action-button"
          :title="action.label"
        >
          <span class="action-icon">{{ action.icon }}</span>
        </button>
      </div>

      <!-- Métadonnées -->
      <div class="message-meta">
        <span v-if="showTimestamp" class="message-time">
          {{ formattedTime }}
        </span>
        <span v-if="statusIcon" class="message-status" :title="statusTitle">
          {{ statusIcon }}
        </span>
      </div>
    </div>

    <!-- Avatar pour nos messages (optionnel) -->
    <div v-if="showAvatar && isOwnMessage" class="message-avatar message-avatar--own">
      <img
        v-if="currentUser.avatar"
        :src="currentUser.avatar"
        :alt="currentUser.name"
        class="avatar-image"
      />
      <div v-else class="avatar-placeholder">
        {{ currentUserInitials }}
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, nextTick } from 'vue'

export default {
  name: 'MessageBubble',
  props: {
    message: {
      type: Object,
      required: true
    },
    currentUser: {
      type: Object,
      default: () => ({})
    },
    showAvatar: {
      type: Boolean,
      default: true
    },
    showTimestamp: {
      type: Boolean,
      default: true
    },
    showUserName: {
      type: Boolean,
      default: false
    },
    repliedMessage: {
      type: Object,
      default: null
    }
  },
  emits: ['action'],
  setup(props, { emit }) {
    const editInput = ref(null)
    const isHovered = ref(false)
    const showActions = ref(false)
    const isEditing = ref(false)
    const editText = ref(props.message.text)

    // Computed
    const isOwnMessage = computed(() => {
      return props.message.userId === props.currentUser?.id
    })

    const bubbleClasses = computed(() => ({
      'message-bubble--own': isOwnMessage.value,
      'message-bubble--other': !isOwnMessage.value,
      'message-bubble--system': props.message.type === 'system',
      'message-bubble--editing': isEditing.value,
      'message-bubble--hovered': isHovered.value,
      'message-bubble--failed': props.message.status === 'failed'
    }))

    const userInitials = computed(() => {
      return props.message.userName
        .split(' ')
        .map(word => word[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
    })

    const currentUserInitials = computed(() => {
      return props.currentUser.name
        ?.split(' ')
        .map(word => word[0])
        .join('')
        .toUpperCase()
        .slice(0, 2) || 'ME'
    })

    const statusTitle = computed(() => {
      const status = props.message.status
      const titles = {
        sending: 'Sending...',
        sent: 'Sent',
        delivered: 'Delivered',
        read: 'Read',
        failed: 'Failed to send'
      }
      return titles[status] || 'Unknown status'
    })

    const statusIcon = computed(() => {
      if (!isOwnMessage.value) return null

      const icons = {
        sending: '⏳',
        sent: '✓',
        delivered: '✓✓',
        read: '✓✓',
        failed: '❌'
      }

      return icons[props.message.status] || icons.sent
    })

    // Formater l'heure
    const formattedTime = computed(() => {
      return new Date(props.message.timestamp).toLocaleTimeString('fr-FR', {
        hour: '2-digit',
        minute: '2-digit'
      })
    })

    // Actions disponibles selon le type de message
    const availableActions = computed(() => {
      const baseActions = []

      if (isOwnMessage.value) {
        baseActions.push(
          { id: 'edit', label: 'Edit', icon: '✏️' },
          { id: 'delete', label: 'Delete', icon: '🗑️' }
        )
      }

      baseActions.push(
        { id: 'reply', label: 'Reply', icon: '↩️' },
        { id: 'react', label: 'React', icon: '😊' }
      )

      return baseActions.filter(action => {
        // Filtrer les actions selon le contexte
        if (action.id === 'edit' && props.message.type !== 'text') return false
        if (action.id === 'delete' && props.message.type === 'system') return false
        return true
      })
    })

    // Méthodes
    const handleAction = (action) => {
      switch (action.id) {
        case 'edit':
          startEdit()
          break
        case 'delete':
          // TODO: Implement delete
          console.log('Delete message:', props.message.id)
          break
        case 'reply':
          // TODO: Implement reply
          console.log('Reply to message:', props.message.id)
          break
        case 'react':
          // Ouvrir le sélecteur de réactions
          break
        default:
          emit('action', { action: action.id, message: props.message })
      }
    }

    const startEdit = () => {
      isEditing.value = true
      editText.value = props.message.text

      nextTick(() => {
        if (editInput.value) {
          editInput.value.focus()
          editInput.value.select()
        }
      })
    }

    const saveEdit = () => {
      if (!editText.value.trim() || editText.value === props.message.text) {
        isEditing.value = false
        return
      }

      // TODO: Implement save edit
      console.log('Save message edit:', props.message.id, editText.value)
      isEditing.value = false
    }

    const cancelEdit = () => {
      isEditing.value = false
      editText.value = props.message.text
    }

    const hasUserReacted = (reaction) => {
      return reaction.userIds?.includes(props.currentUser.id) || false
    }

    const toggleReaction = (reaction) => {
      if (hasUserReacted(reaction)) {
        // TODO: Implement remove reaction
        console.log('Remove reaction:', reaction.emoji, 'from message:', props.message.id)
      } else {
        // TODO: Implement add reaction
        console.log('Add reaction:', reaction.emoji, 'to message:', props.message.id)
      }
    }

    const getReactionUsers = (reaction) => {
      if (!reaction.userIds || reaction.userIds.length === 0) {
        return `${reaction.emoji} ${reaction.count}`
      }

      const names = reaction.userIds.slice(0, 3).map(userId => {
        // Dans une vraie implémentation, récupérer le nom de l'utilisateur
        return `User ${userId.slice(-4)}`
      })

      if (reaction.userIds.length > 3) {
        names.push(`+${reaction.userIds.length - 3} others`)
      }

      return `${reaction.emoji} ${names.join(', ')}`
    }

    const formatFileSize = (bytes) => {
      if (!bytes) return 'Unknown size'

      const units = ['B', 'KB', 'MB', 'GB']
      let size = bytes
      let unitIndex = 0

      while (size >= 1024 && unitIndex < units.length - 1) {
        size /= 1024
        unitIndex++
      }

      return `${size.toFixed(1)} ${units[unitIndex]}`
    }

    const previewAttachment = (attachment) => {
      // Ouvrir un aperçu de l'image
      window.open(attachment.url, '_blank')
    }

    const downloadAttachment = (attachment) => {
      // Télécharger le fichier
      const link = document.createElement('a')
      link.href = attachment.url
      link.download = attachment.name
      link.click()
    }

    // Gestion du survol
    const handleMouseEnter = () => {
      isHovered.value = true
      showActions.value = true
    }

    const handleMouseLeave = () => {
      isHovered.value = false
      showActions.value = false
    }

    return {
      // Références
      editInput,

      // État local
      isHovered,
      showActions,
      isEditing,
      editText,

      // Computed
      bubbleClasses,
      userInitials,
      currentUserInitials,
      statusTitle,
      statusIcon,
      formattedTime,
      availableActions,
      isOwnMessage,

      // Méthodes
      handleAction,
      startEdit,
      saveEdit,
      cancelEdit,
      hasUserReacted,
      toggleReaction,
      getReactionUsers,
      formatFileSize,
      previewAttachment,
      downloadAttachment,
      handleMouseEnter,
      handleMouseLeave
    }
  }
}
</script>

<style scoped>
.message-bubble {
  display: flex;
  gap: 0.5rem;
  max-width: 70%;
  position: relative;
  transition: all 0.2s ease;
}

.message-bubble--own {
  flex-direction: row-reverse;
}

.message-bubble--other {
  flex-direction: row;
}

.message-bubble--system {
  justify-content: center;
  max-width: 80%;
}

.message-bubble--system .message-content {
  background: #f3f4f6;
  color: #6b7280;
  font-style: italic;
  text-align: center;
}

.message-avatar {
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  margin-top: 0.25rem;
}

.message-avatar--own {
  margin-left: 0.5rem;
}

.avatar-image {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
}

.avatar-placeholder {
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 0.75rem;
  font-weight: 600;
}

.message-content {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
}

.message-bubble--own .message-content {
  align-items: flex-end;
}

.message-bubble--other .message-content {
  align-items: flex-start;
}

.message-username {
  font-size: 0.75rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.125rem;
  padding: 0 0.5rem;
}

.message-bubble--own .message-username {
  display: none; /* Pas besoin de voir notre propre nom */
}

.message-reply {
  background: rgba(0, 0, 0, 0.05);
  border-radius: 0.5rem;
  padding: 0.5rem;
  margin-bottom: 0.25rem;
  border-left: 3px solid hsl(var(--primary));
}

.reply-indicator {
  font-size: 0.75rem;
  color: hsl(var(--primary));
  margin-bottom: 0.25rem;
}

.reply-text {
  font-size: 0.875rem;
  color: #6b7280;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
}

.message-body {
  background: #f3f4f6;
  border-radius: 1rem;
  padding: 0.75rem 1rem;
  position: relative;
  word-wrap: break-word;
}

.message-bubble--own .message-body {
  background: hsl(var(--primary));
  color: white;
}

.message-bubble--system .message-body {
  background: transparent;
  padding: 0.5rem 0.75rem;
}

.message-bubble--failed .message-body {
  background: #fee2e2;
  color: #dc2626;
}

.message-text {
  line-height: 1.4;
  white-space: pre-wrap;
}

.edit-input {
  width: 100%;
}

.edit-field {
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 0.25rem;
  padding: 0.25rem 0.5rem;
  font-size: inherit;
  font-family: inherit;
  outline: none;
  width: 100%;
  min-width: 200px;
}

.edit-field:focus {
  border-color: hsl(var(--primary));
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
}

.message-system {
  font-size: 0.875rem;
  text-align: center;
}

.message-attachments {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 0.5rem;
}

.attachment {
  border-radius: 0.5rem;
  overflow: hidden;
  background: rgba(0, 0, 0, 0.05);
}

.attachment-image {
  width: 100%;
  max-width: 200px;
  height: auto;
  cursor: pointer;
  transition: transform 0.2s;
}

.attachment-image:hover {
  transform: scale(1.02);
}

.attachment-file {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 0.5rem;
}

.file-icon {
  font-size: 1.5rem;
  flex-shrink: 0;
}

.file-info {
  flex: 1;
  min-width: 0;
}

.file-name {
  font-size: 0.875rem;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.file-size {
  font-size: 0.75rem;
  opacity: 0.7;
  margin-top: 0.125rem;
}

.download-button {
  background: none;
  border: none;
  padding: 0.25rem;
  border-radius: 0.25rem;
  cursor: pointer;
  transition: background 0.2s;
}

.download-button:hover {
  background: rgba(255, 255, 255, 0.2);
}

.message-reactions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  margin-top: 0.25rem;
}

.reaction-button {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.5rem;
  background: rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 1rem;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 0.875rem;
}

.message-bubble--own .reaction-button {
  background: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.3);
}

.reaction-button:hover {
  transform: scale(1.05);
}

.reaction-button--active {
  background: rgba(59, 130, 246, 0.2);
  border-color: hsl(var(--primary));
}

.reaction-emoji {
  font-size: 1rem;
}

.reaction-count {
  font-size: 0.75rem;
  font-weight: 500;
}

.message-actions {
  display: flex;
  gap: 0.25rem;
  margin-top: 0.25rem;
  opacity: 0;
  transition: opacity 0.2s;
}

.message-bubble--hovered .message-actions {
  opacity: 1;
}

.action-button {
  width: 28px;
  height: 28px;
  background: rgba(0, 0, 0, 0.05);
  border: none;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
}

.message-bubble--own .action-button {
  background: rgba(255, 255, 255, 0.2);
}

.action-button:hover {
  background: rgba(0, 0, 0, 0.1);
  transform: scale(1.1);
}

.message-bubble--own .action-button:hover {
  background: rgba(255, 255, 255, 0.3);
}

.action-icon {
  font-size: 0.875rem;
}

.message-meta {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.75rem;
  color: #9ca3af;
  margin-top: 0.125rem;
  padding: 0 0.5rem;
}

.message-bubble--own .message-meta {
  color: rgba(255, 255, 255, 0.7);
}

.message-time {
  white-space: nowrap;
}

.message-status {
  display: flex;
  align-items: center;
}

/* Responsive */
@media (max-width: 640px) {
  .message-bubble {
    max-width: 85%;
  }

  .message-avatar {
    width: 28px;
    height: 28px;
  }

  .message-body {
    padding: 0.625rem 0.875rem;
  }

  .attachment-image {
    max-width: 150px;
  }
}
</style>